
import React, { useState } from 'react';
import { UserProfile, Language, Theme } from '../types';
import { translations } from '../i18n';
import { GlassCard, Button } from '../components/shared';
import { X, Save, User, Star, Hash, Rocket, Zap, Palette, Music, Book, Cpu } from 'lucide-react';
import { useWonderEngine } from '../hooks/useWonderEngine';

interface StudentProfileProps {
  user: UserProfile;
  lang: Language;
  theme?: Theme; // New prop for theme awareness
  onClose: () => void;
}

const AVATARS = [
  { id: 'rocket', icon: '🚀', color: 'bg-blue-500' },
  { id: 'alien', icon: '👽', color: 'bg-green-500' },
  { id: 'robot', icon: '🤖', color: 'bg-slate-500' },
  { id: 'cat', icon: '🐱', color: 'bg-orange-500' },
  { id: 'dino', icon: '🦖', color: 'bg-emerald-600' },
  { id: 'star', icon: '⭐', color: 'bg-yellow-400' },
];

const INTEREST_TAGS = [
  { id: 'space', label: { en: 'Space', zh: '太空' }, icon: Rocket },
  { id: 'dinos', label: { en: 'Dinosaurs', zh: '恐龙' }, icon: Zap },
  { id: 'art', label: { en: 'Drawing', zh: '绘画' }, icon: Palette },
  { id: 'music', label: { en: 'Music', zh: '音乐' }, icon: Music },
  { id: 'coding', label: { en: 'Coding', zh: '编程' }, icon: Cpu },
  { id: 'reading', label: { en: 'Reading', zh: '阅读' }, icon: Book },
];

export const StudentProfile: React.FC<StudentProfileProps> = ({ user, lang, theme = 'light', onClose }) => {
  const { actions } = useWonderEngine(lang);
  const t = translations[lang];

  const [name, setName] = useState(user.name);
  const [age, setAge] = useState(user.age || 7);
  const [grade, setGrade] = useState(user.grade);
  const [selectedAvatar, setSelectedAvatar] = useState(user.avatar || 'rocket');
  const [selectedInterests, setSelectedInterests] = useState<string[]>(user.interests || []);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await actions.updateProfile({
      name,
      age,
      grade,
      avatar: selectedAvatar,
      interests: selectedInterests
    });
    setLoading(false);
    onClose();
  };

  const toggleInterest = (id: string) => {
    if (selectedInterests.includes(id)) {
      setSelectedInterests(prev => prev.filter(i => i !== id));
    } else {
      if (selectedInterests.length < 5) {
        setSelectedInterests(prev => [...prev, id]);
      }
    }
  };

  // Determine container styles based on theme
  const containerClass = theme === 'dark' 
    ? 'bg-slate-900/90 text-white' 
    : 'bg-slate-50/90 text-slate-900';
    
  const inputClass = theme === 'dark'
    ? 'bg-slate-950/50 border-white/10 text-white focus:border-indigo-500'
    : 'bg-white border-slate-200 text-slate-900 focus:border-indigo-500 shadow-sm';

  const labelClass = theme === 'dark'
    ? 'text-slate-400'
    : 'text-slate-500';

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md p-4 animate-in fade-in duration-300 ${containerClass}`}>
      <GlassCard variant={theme === 'dark' ? 'dark' : 'light'} className="w-full max-w-lg p-0 overflow-hidden shadow-2xl border-white/20">
        
        {/* Header (Holographic ID Style) */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-10 bg-white/10 rounded-full blur-2xl transform translate-x-10 -translate-y-10"></div>
          <div className="flex justify-between items-start relative z-10">
             <div>
               <h2 className="text-2xl font-black font-orbitron text-white uppercase tracking-wider">Commander ID</h2>
               <p className="text-indigo-200 text-xs font-bold tracking-[0.2em] opacity-80">ACCESS GRANTED</p>
             </div>
             <button onClick={onClose} className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors text-white">
               <X size={20}/>
             </button>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-8 max-h-[80vh] overflow-y-auto">
          
          {/* 1. Avatar Selection */}
          <div className="flex flex-col items-center">
             <div className="relative mb-6 group">
                <div className={`w-28 h-28 rounded-full flex items-center justify-center text-6xl shadow-xl transition-transform ${AVATARS.find(a => a.id === selectedAvatar)?.color || 'bg-slate-700'}`}>
                   {AVATARS.find(a => a.id === selectedAvatar)?.icon}
                </div>
                <div className="absolute -bottom-2 -right-2 bg-slate-900 text-white text-[10px] px-2 py-1 rounded-full font-bold border border-white/20">
                   Lv.{user.level}
                </div>
             </div>
             
             <div className="flex gap-3 overflow-x-auto pb-2 w-full justify-center">
                {AVATARS.map(av => (
                  <button 
                    key={av.id}
                    onClick={() => setSelectedAvatar(av.id)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-all border-2 ${selectedAvatar === av.id ? 'border-indigo-500 scale-110 shadow-lg' : 'border-transparent opacity-50 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5'}`}
                  >
                    {av.icon}
                  </button>
                ))}
             </div>
          </div>

          {/* 2. Basic Info Inputs */}
          <div className="grid grid-cols-2 gap-4">
             <div className="col-span-2">
                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${labelClass}`}>{t.name}</label>
                <div className="relative">
                   <User className={`absolute left-4 top-3.5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} size={18}/>
                   <input 
                     value={name} 
                     onChange={(e) => setName(e.target.value)}
                     className={`w-full border rounded-xl py-3 pl-12 pr-4 font-bold focus:ring-1 focus:ring-indigo-500 outline-none transition-all ${inputClass}`}
                   />
                </div>
             </div>
             
             <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${labelClass}`}>{t.age}</label>
                <div className="relative">
                   <Star className={`absolute left-4 top-3.5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} size={18}/>
                   <input 
                     type="number"
                     value={age} 
                     onChange={(e) => setAge(Number(e.target.value))}
                     className={`w-full border rounded-xl py-3 pl-12 pr-4 font-bold focus:ring-1 focus:ring-indigo-500 outline-none transition-all ${inputClass}`}
                   />
                </div>
             </div>

             <div>
                <label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${labelClass}`}>{t.grade}</label>
                <div className="relative">
                   <Hash className={`absolute left-4 top-3.5 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} size={18}/>
                   <select 
                     value={grade} 
                     onChange={(e) => setGrade(Number(e.target.value))}
                     className={`w-full border rounded-xl py-3 pl-12 pr-4 font-bold focus:ring-1 focus:ring-indigo-500 outline-none transition-all appearance-none ${inputClass}`}
                   >
                     {[1,2,3,4,5,6].map(g => <option key={g} value={g}>Grade {g}</option>)}
                   </select>
                </div>
             </div>
          </div>

          {/* 3. Interests Selection */}
          <div>
             <label className={`block text-xs font-bold uppercase tracking-wider mb-3 ${labelClass}`}>
               {t.interests} <span className="text-slate-600 font-normal ml-1">({selectedInterests.length}/5)</span>
             </label>
             <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
               {INTEREST_TAGS.map(tag => {
                 const isSelected = selectedInterests.includes(tag.id);
                 const Icon = tag.icon;
                 // Dynamic styling for selection based on theme
                 let btnClass = "";
                 if (isSelected) {
                    btnClass = "bg-indigo-600 border-indigo-500 text-white shadow-md";
                 } else {
                    btnClass = theme === 'dark' 
                        ? "bg-slate-950/30 border-white/10 text-slate-400 hover:border-white/30" 
                        : "bg-white border-slate-200 text-slate-500 hover:border-indigo-200 hover:bg-slate-50";
                 }

                 return (
                   <button
                     key={tag.id}
                     onClick={() => toggleInterest(tag.id)}
                     className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${btnClass}`}
                   >
                     <Icon size={16} />
                     <span className="text-xs font-bold">{lang === 'zh' ? tag.label.zh : tag.label.en}</span>
                   </button>
                 )
               })}
             </div>
             <p className="text-[10px] text-slate-500 mt-2 italic text-center">
               * {lang === 'zh' ? '诺诺会根据兴趣定制内容哦！' : 'Nono will customize lessons based on these!'}
             </p>
          </div>

        </div>

        {/* Footer Actions */}
        <div className={`p-6 border-t flex gap-4 ${theme === 'dark' ? 'border-white/10 bg-slate-900/50' : 'border-slate-100 bg-slate-50'}`}>
           <Button variant="secondary" onClick={onClose} className="flex-1">
              {t.cancel}
           </Button>
           <Button onClick={handleSave} disabled={loading} className="flex-[2] shadow-indigo-500/40">
              {loading ? t.saving : t.saveProfile}
           </Button>
        </div>

      </GlassCard>
    </div>
  );
};
